export type NorPlanId = "nor_solo" | "nor_starter" | "nor_team" | "nor_enterprise";

export type NorPlan = {
  id: NorPlanId;
  name: string;
  tagline: string;
  monthlyPriceId: string;
  yearlyPriceId: string | null;
  /** null quando o plano não tem preço público (ver `quoteOnly`). */
  monthlyCents: number | null;
  yearlyCents: number | null;
  maxSeats: number | null;
  /** Preço sob consulta: não exibe valor e não tem caminho de checkout. */
  quoteOnly?: boolean;
  features: string[];
  highlight?: boolean;
  ctaLabel?: string;
};

export const NOR_PLANS: NorPlan[] = [
  {
    id: "nor_solo",
    name: "Solo",
    tagline: "Corretor autônomo.",
    monthlyPriceId: "nor_solo_monthly",
    yearlyPriceId: "nor_solo_yearly",
    monthlyCents: 9900,
    yearlyCents: 99000,
    maxSeats: 1,
    features: [
      "1 corretor",
      "Leads ilimitados, CRM completo",
      "Alertas push + e-mail em tempo real",
      "Playbook IA por lead",
      "Export CSV (Leads & Negócios)",
    ],
  },
  {
    id: "nor_starter",
    name: "Starter",
    tagline: "Imobiliária pequena.",
    monthlyPriceId: "nor_starter_monthly",
    yearlyPriceId: "nor_starter_yearly",
    monthlyCents: 19900,
    yearlyCents: 199000,
    maxSeats: 3,
    features: [
      "Até 3 corretores",
      "Tudo do Solo",
      "Pipeline & negócios compartilhados",
      "Convites de membros",
    ],
  },
  {
    id: "nor_team",
    name: "Team",
    tagline: "Imobiliária em crescimento.",
    monthlyPriceId: "nor_team_monthly",
    yearlyPriceId: "nor_team_yearly",
    monthlyCents: 59900,
    yearlyCents: 599000,
    maxSeats: 15,
    features: [
      "Até 15 corretores",
      "Painel do gestor (SLA, performance, metas)",
      "Auto-assign round-robin / least-loaded",
      "Alertas SLA proativos",
      "Suporte prioritário",
    ],
    highlight: true,
  },
  {
    id: "nor_enterprise",
    name: "Enterprise",
    tagline: "Rede ou imobiliária grande.",
    monthlyPriceId: "nor_enterprise_monthly",
    yearlyPriceId: null,
    monthlyCents: null,
    quoteOnly: true,
    yearlyCents: null,
    maxSeats: null,
    features: [
      "Corretores ilimitados, sem teto de assentos",
      "Tudo do Team",
      "API de importação de catálogo (chaves por workspace, upsert por origem)",
      "Suporte prioritário com canal dedicado",
    ],
    ctaLabel: "Fale conosco",
  },
];

export function findNorPlan(productId: string | null | undefined): NorPlan | undefined {
  if (!productId) return undefined;
  return NOR_PLANS.find((p) => p.id === productId);
}

/**
 * Vocabulário legado de `org_entitlements.plan` ("solo" | "team" | "enterprise")
 * → catálogo comercial atual. Aceita também os ids `nor_*`.
 */
const LEGACY_PLAN_MAP: Record<string, NorPlanId> = {
  solo: "nor_solo",
  starter: "nor_starter",
  team: "nor_team",
  enterprise: "nor_enterprise",
};

export function planFromWorkspace(plan: string | null | undefined): NorPlan | undefined {
  if (!plan) return undefined;
  return findNorPlan(LEGACY_PLAN_MAP[plan] ?? plan);
}

export function formatBRL(cents: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

/** Rótulo de preço para exibição. Planos `quoteOnly` não mostram valor. */
export const QUOTE_LABEL = "Sob consulta";

export function planPriceLabel(plan: NorPlan, cycle: "month" | "year") {
  if (plan.quoteOnly || plan.monthlyCents === null) {
    return { price: QUOTE_LABEL, suffix: "", quoteOnly: true as const };
  }
  const yearly = cycle === "year" && plan.yearlyPriceId && plan.yearlyCents !== null;
  return {
    price: formatBRL(yearly ? plan.yearlyCents! : plan.monthlyCents),
    suffix: yearly ? "/ano" : "/mês",
    quoteOnly: false as const,
  };
}
